<template>
  <div class="card border-0 bg-white profile-settings-card rounded-0">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <form v-on:submit="submit">
        <div class="row">
          <!-- NAME -->
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                {{ $t('form.name.label') }}*
              </label>
              <input v-model="role.name" class="form-control shadow-none text-black" required
                :class="[{ 'is-invalid': submitted && errors?.name }]" />
              <!-- ERRORS -->
              <div class="form-messages mt-2 px-1" v-if="errors?.name ?? false">
                <p v-for="error in errors.name" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                  error }}</p>
              </div>
            </div>
          </div>
          <div class="col-12"></div>
          <!-- ROLE -->
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                {{ $t('feature.roles.type') }}*
              </label>
              <div>
                <select class="form-select shadow-none text-black fs-md-15" v-model="role.type" required :disabled="update"
                  :class="[{ 'is-invalid': submitted && errors?.type }]">
                  <option v-for="type in types" :value="type" :key="type">{{ $t(`feature.roles.$type.${type}`) }}
                  </option>
                </select>
                <!-- ERRORS -->
                <div class="form-messages mt-2 px-1" v-if="errors?.type ?? false">
                  <p v-for="error in errors.type" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                    error }}</p>
                </div>
              </div>
            </div>
          </div>
          <div class="col-12"></div>
          <!-- ZONE -->
          <div class="col-md-6" v-if="role.type === Role.internal">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                {{ $t('feature.roles.zone') }}
              </label>
              <div>
                <select class="form-select shadow-none text-black fs-md-15" v-model="role.zone_id"
                  :class="[{ 'is-invalid': submitted && errors?.zone_id }]">
                  <option  :value="null" :key="-1">{{  }}
                  </option>
                  <option v-for="zone in zones" :value="zone.id" :key="zone.id!">{{ zone.name }}
                  </option>
                </select>
                <!-- ERRORS -->
                <div class="form-messages mt-2 px-1" v-if="errors?.zone_id ?? false">
                  <p v-for="error in errors.zone_id" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                    error }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="col-12" v-if="loading">
            <SpinnerLoading />
          </div>
          <div class="col-md-12" v-else>
            <div class="d-flex align-items-center justify-content-between gap-3">
              <!-- DELETE -->
              <button v-if="update" type="button" class="bg-transparent p-0 border-0 text-danger lh-1 fw-medium"
                data-bs-toggle="modal" data-bs-target="#confirmModal">
                <i class="ph ph-trash lh-1 me-1 position-relative top-2"></i>
                <span class="position-relative">{{ $t('cta.delete_role') }}</span>
              </button>
              <span v-else></span>
              <!-- SAVE -->
              <div class="d-flex flex-wrap gap-3 justify-content-end ms-auto">
                <DiscardButton @confirm="$router.push({ path: '/system-roles' })" />
                <button
                  class="default-btn transition fw-medium text-white pt-10 pb-10 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-35 pe-md-35 fs-md-15 fs-lg-16"
                  type="submit" v-on:click="submitted = true">
                  {{ $t('cta.save_role') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
  <!-- ERROR MESSAGE -->
  <div class="row justify-content-center">
    <div class="col">
      <div
        class="alert alert-danger alert-dismissible fade border-0 border-danger-subtle border-top rounded-0 m-0 mb-25"
        role="alert" id="errorMessage">
        <strong>{{ $t('error.something_went_wrong') }}</strong>
        <button type="button" class="btn-close" v-on:click="hideError" aria-label="Close"></button>
      </div>
    </div>
  </div>
  <ConfirmModal @confirm="deleteRole()" :title="$t('cta.$delete.title')" :message="$t('cta.$delete.message')" />
  <RolePermissions v-if="update && (role as RoleInfo).type == Role.hcp" :id="(role as RoleInfo).id" />
  <RoleConsents :role="role as RoleInfo" v-if="update && (role as RoleInfo).type != Role.admin" :id="(role as RoleInfo).id" />
</template>

<script lang="ts">
import { defineComponent } from "vue";
import ConfirmModal from "@/components/Widgets/ConfirmModal.vue";
import SpinnerLoading from "@/components/Widgets/Spinner.vue";
import RolePermissions from "@/components/FK/Roles/Permissions/RolePermissions.vue";
import { Role, RoleBaseInfo, RoleInfo } from "@/utils/models/user.model";
import { removeNullProperties } from "@/utils/methods/remove_null_properties";
import API from "@/services/api/api.service";
import DiscardButton from "@/components/Widgets/DiscardButton.vue";
import RoleConsents from "./RoleConsents.vue";
import { Zone } from "@/utils/models/zone.model";

export default defineComponent({
  name: "RoleForm",
  components: {
    ConfirmModal,
    SpinnerLoading,
    RolePermissions,
    DiscardButton,
    RoleConsents
  },
  data: function () {
    return {
      role: (this.$route.meta.role ?? {}) as RoleBaseInfo,
      zones: (this.$route.meta.zones ?? []) as Zone[],
      submitted: false,
      loading: false,
      errors: undefined as any,
      types: Object.values(Role),
    }
  },
  computed: {
    Role() {return Role},
    update() {
      return this.$route.meta.role !== undefined && this.$route.meta.role !== null;
    }
  },
  methods: {
    submit(event): void {
      this.errors = undefined;
      event.preventDefault();
      console.log("ROLE", this.role);

      removeNullProperties<RoleBaseInfo>(this.role);

      this.loading = true;
      if (this.update) {
        console.log('UPDATE');
        API.updateRole((this.role as RoleInfo)).then((response) => {
          this.$router.push(`/system-roles`);
        }).catch((error) => {
          this.errors = error.response?.data?.errors;
          this.showError();
        }).finally(() => {
          this.loading = false;
        });
      } else {
        console.log('CREATE');
        API.createRole(this.role).then((response) => {
          if (response?.data?.success === true && response?.data?.data?.id !== undefined) {
            this.$router.push(`/role/${response.data.data.id}/edit`);
          }
        }).catch((error) => {
          this.errors = error.response?.data?.errors;
          this.showError();
        }).finally(() => {
          this.loading = false;
        });
      }
    },
    deleteRole(): void {
      this.loading = true;
      API.deleteRole((this.role as RoleInfo).id!).then((response) => {
        if (response.data.success === true) {
          this.$router.push('/system-roles');
        }
      }).catch((error) => {
        this.showError();
      }).finally(() => {
        console.log("DELETED_USER_ID", (this.role as RoleInfo).id);
        this.loading = false;
      })
    },
    showError() {
      const error = document.getElementById('errorMessage');
      error?.classList.add('show');
      setTimeout(() => {
        this.hideError();
      }, 5000)
    },
    hideError() {
      const error = document.getElementById('errorMessage');
      error?.classList.remove('show');
    }
  },
});
</script>